/**
 * Shared pieces of the built-in effect shaders (W2-01b; formats-and-packaging.md P§5.2, rendering.md R§9).
 *
 * Every built-in is our own GLSL ES 1.00 fragment shader written from the behavioural description of Phira's
 * built-ins (no Phira shader source is used; they are badged "approximate"). They follow Phira's effect contract
 * exactly like a custom shader from a package:
 *
 *  - `varying vec2 uv` from the fixed vertex shader, (0, 0) = bottom-left, `uv = (texcoord - 0.5) * UVScale + 0.5`;
 *  - `uniform sampler2D screenTexture` (the frame so far), and only when used `uniform float time` (chart seconds)
 *    and `uniform vec2 screenSize` (render-target pixels);
 *  - every other uniform is a float or vec4 declared as `uniform <type> <name>; // %<default>% <hint>` (Phira's
 *    default-comment syntax); the same defaults are in `BuiltinShaderDef.defaults`, applied before the effect's vars.
 *
 * Sources start with a precision block (no `#version` line: GLSL ES 1.00 is the default, and the runner may inject
 * its own precision statement in front).
 */

import type { BuiltinShader } from '../../../../core/model/types';
import type { MessageRef } from '../../../../core/util/types';
import type { BuiltinShaderDef } from '../../../types';
import { m } from './builtinFx.messages';
import { signOffOf } from './signOff';

export type UniformValue = number | readonly number[];
export type UniformType = 'float' | 'vec2' | 'vec4';
/** Renderer-provided inputs a built-in reads besides `uv` and `screenTexture`. */
export type ScreenInput = 'time' | 'screenSize';

export interface BuiltinUniform {
  readonly name: string;
  readonly type: UniformType;
  readonly default: UniformValue;
  /** Free-text hint after the default in the source comment (Phira does not parse it), e.g. '0..1', 'int 1..64'. */
  readonly hint?: string;
  /** Editor range hints (sliders); values outside still work. */
  readonly min?: number;
  readonly max?: number;
  /** Used as a whole number (rounded) by the shader. */
  readonly integer?: boolean;
  readonly label: MessageRef;
}

/** A built-in effect: the contract's BuiltinShaderDef plus what the editor shows about it. */
export interface BuiltinEffect extends BuiltinShaderDef {
  readonly label: MessageRef;
  readonly description: MessageRef;
  readonly uniforms: readonly BuiltinUniform[];
  readonly inputs: readonly ScreenInput[];
  /**
   * 'approximate': reimplemented from behaviour and not yet verified against Phira captures (DESIGN D24, R13);
   * 'verified': a person compared it with Phira captures and signed it off (signOff.ts, Appendix C H5).
   */
  readonly fidelity: BuiltinFidelity;
}

/** How faithful the preview of a reimplemented built-in is. */
export type BuiltinFidelity = 'approximate' | 'verified';

/** Uniforms the renderer sets itself (never in `defaults`). */
export const RESERVED_UNIFORMS: readonly string[] = ['screenTexture', 'time', 'screenSize', 'UVScale'];

/** The badge every built-in effect carries in the editor. */
export const APPROXIMATE_BADGE: { readonly label: MessageRef; readonly hint: MessageRef } = {
  label: { key: m.approximate },
  hint: { key: m.approximateHint },
};

export const PRELUDE = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 uv;
uniform sampler2D screenTexture;`;

const INPUT_DECL: Readonly<Record<ScreenInput, string>> = {
  time: 'uniform float time;',
  screenSize: 'uniform vec2 screenSize;',
};

/** Reads the screen with the coordinate clamped to the texture (edge pixels repeat outside). */
export const SAMPLE_SCREEN = `vec4 sampleScreen(vec2 p) {
  return texture2D(screenTexture, clamp(p, 0.0, 1.0));
}`;

/** Rec. 601 luma. */
export const LUMA = `float luma(vec3 c) {
  return dot(c, vec3(0.299, 0.587, 0.114));
}`;

/** Deterministic pseudo-random value in [0, 1) from a 2D input (arithmetic only, no sin). */
export const HASH12 = `float hash12(vec2 p) {
  vec3 q = fract(vec3(p.x, p.y, p.x + p.y) * vec3(0.1031, 0.1030, 0.0973));
  q += dot(q, q.yzx + 19.19);
  return fract((q.x + q.y) * q.z);
}`;

/** A GLSL float literal (always with a decimal point). */
export function glslFloat(v: number): string {
  if (!Number.isFinite(v)) throw new Error(`not a finite number: ${String(v)}`);
  const s = String(v);
  return /[.eE]/.test(s) ? s : `${s}.0`;
}

/** The `%default%` text of a uniform default (vectors as comma-separated floats). */
export function defaultText(v: UniformValue): string {
  return typeof v === 'number' ? glslFloat(v) : v.map(glslFloat).join(', ');
}

/** `uniform <type> <name>; // %<default>% <hint>` */
export function uniformDecl(u: BuiltinUniform): string {
  const hint = u.hint === undefined ? '' : ` ${u.hint}`;
  return `uniform ${u.type} ${u.name}; // %${defaultText(u.default)}%${hint}`;
}

export interface BuiltinSpec {
  readonly name: BuiltinShader;
  readonly label: MessageRef;
  readonly description: MessageRef;
  readonly inputs: readonly ScreenInput[];
  readonly uniforms: readonly BuiltinUniform[];
  /** GLSL helper functions placed before main (SAMPLE_SCREEN, LUMA, HASH12, ...). */
  readonly helpers?: readonly string[];
  /** `void main() { ... }` */
  readonly main: string;
}

function checkSpec(spec: BuiltinSpec): void {
  for (const u of spec.uniforms) {
    if (RESERVED_UNIFORMS.includes(u.name)) throw new Error(`${spec.name}: ${u.name} is set by the renderer`);
    const len = typeof u.default === 'number' ? 1 : u.default.length;
    const want = u.type === 'float' ? 1 : u.type === 'vec2' ? 2 : 4;
    if (len !== want) throw new Error(`${spec.name}: default of ${u.name} does not match ${u.type}`);
  }
}

/** Builds a built-in effect: source (prelude, inputs, uniforms with default comments, helpers, main) + defaults. */
export function defineBuiltin(spec: BuiltinSpec): BuiltinEffect {
  checkSpec(spec);
  const fragment = [
    PRELUDE,
    ...spec.inputs.map((i) => INPUT_DECL[i]),
    ...spec.uniforms.map(uniformDecl),
    '',
    ...(spec.helpers ?? []).flatMap((h) => [h, '']),
    spec.main.trim(),
    '',
  ].join('\n');
  const defaults: Record<string, UniformValue> = {};
  for (const u of spec.uniforms) defaults[u.name] = typeof u.default === 'number' ? u.default : [...u.default];
  return {
    name: spec.name,
    fragment,
    defaults,
    label: spec.label,
    description: spec.description,
    uniforms: spec.uniforms,
    inputs: spec.inputs,
    fidelity: signOffOf(spec.name) === null ? 'approximate' : 'verified',
  };
}
